import { observer } from "mobx-react";
import { runInAction } from "mobx";
import Scaffold from "../../../../components/Scaffold";
import { Button, Form, Input, message, Modal, Select } from "antd";
import AdminTariffsStore from "../../../../stores/admin/adminTariffsStore";
import styles from "./index.module.scss";
import { useEffect } from "react";
import Utils from "../../../../utils/Utils";
import DropdownActions from "../../../../components/DropdownActions";
import { PencilSquare, Trash2 } from "react-bootstrap-icons";

const store = new AdminTariffsStore();

const AdminTariffsView = observer(() => {
  const [form] = Form.useForm();

  useEffect(() => {
    store.findAll();
    return () => {
      runInAction(() => {
        store.data = undefined;
        store.state = "None";
      });
    };
  }, []);

  const onConfirm = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      if (values.tariffCountryPrices) {
        values.tariffCountryPrices = values.tariffCountryPrices.map((c: any) => ({
          ...c,
        }));
      }

      if (store.state === "Create") {
        await store.onCreate(values);
      }
      if (store.state === "Edit") {
        await store.onUpdate(values);
      }
      message.success("Данные сохранены");
    } catch (error: any) {
      if (error.response?.data?.messages) {
          if (error.response?.data?.messages) {
            const errors = Object.entries(error.response.data.messages).map(([field, messages]) => {
              const fieldPath = field.split(".").map((part) => (isNaN(Number(part)) ? part : Number(part)));
              return {
                name: fieldPath,
                errors: messages as string[],
              };
            });

            form.setFields(errors);
          }
        }

      console.error(error);
      
      message.error("Что-то пошло не так");
    }
  };

  const onCreate = async () => {
    await store.findCreate();
    form.resetFields();
    runInAction(() => {
      store.state = "Create";
    });
  };

  const onCancel = () => {
    runInAction(() => {
      store.state = "None";
    });
  };

  const onEdit = async (id: string) => {
    await store.onRead(id);
    await store.findCreate();
    const policies = store.item?.policies.map((e) => e.id);
    const limits = store.item?.limits.map((limit) => ({
      key: limit.key.value,
      value: limit.value,
    }));
    const countryPrices = store.item?.tariffCountryPrices?.map((p) => ({
      countryId: p.country.id,
      price: p.price,
      oldPrice: p.oldPrice,
    }));

    form.setFieldsValue({
      ...store.item,
      policyIds: policies,
      limits,
      tariffCountryPrices: countryPrices,
      systemIdentifier: store.item?.systemIdentifier,
    });
    runInAction(() => {
      store.state = "Edit";
    });
  };

  const onRemove = (id: string) => {
    Modal.confirm({
      title: "Вы уверены?",
      okText: "Удалить",
      onOk: async () => {
        await store.onDelete(id);
        message.success("Тариф удален");
      },
    });
  };

  return (
    <Scaffold
      title="Тарифы"
      trailing={
        <Button onClick={onCreate} type="primary">
          Добавить
        </Button>
      }
    >
      <div className={styles.grid}>
        {store.data?.map((e) => (
          <div className={styles.card} key={e.id}>
            <div className={styles.header}>
              <div className={styles.title}>{e.name}</div>
              <DropdownActions
                actions={[
                  {
                    label: "Редактировать",
                    icon: <PencilSquare />,
                    onClick: () => onEdit(e.id),
                  },
                  {
                    label: "Удалить",
                    icon: <Trash2 />,
                    onClick: () => onRemove(e.id),
                  },
                ]}
              />
            </div>
            <div className={styles.info}>
              <div className={styles.limits}>
                <div className={styles.list}>
                  <div className={styles.title}>{`Ограничения`}</div>
                  {e.limits.map((limit) => (
                    <div key={limit.id}>{`${limit.key.title} - ${limit.value}`}</div>
                  ))}
                </div>
              </div>
              <div className={styles.routes}>
                <div className={styles.list}>
                  <div className={styles.title}>{`Метрики`}</div>
                  {e.policies.map((policy) => (
                    <div key={policy.id}>{policy.name}</div>
                  ))}
                </div>
              </div>
            </div>
            {(e.createdAt || e.price || e.oldPrice) && (
              <div className={styles.created}>
                {e.createdAt && <span>{`Дата создания: ${Utils.format(e.createdAt, "DD.MM.YYYY")}`}</span>}
                {(e.price || e.oldPrice) && (
                  <div className={styles.prices}>
                    {e.oldPrice && <span className={styles.oldPrice}>{`${e.oldPrice} ₽`}</span>}
                    {e.price && <span className={styles.newPrice}>{`${e.price} ₽`}</span>}
                  </div>
                )}
              </div>
            )}
          </div>
          
        ))}
      </div>
      <Modal
        open={store.state !== "None"}
        title={store.state === "Create" ? "Создать" : "Редактировать"}
        onCancel={onCancel}
        onOk={onConfirm}
        width={600}
        okButtonProps={{ disabled: store.isLoading }}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Название" name="name" rules={[{ required: true, message: " " }]}>
            <Input />
          </Form.Item>
          <Form.Item
            label="Актуальная цена"
            name="price"
            rules={[
              {
                required: !form.getFieldValue("systemIdentifier"),
                message: " ",
              },
            ]}
          >
            <Input type="number" min={0} />
          </Form.Item>
          <Form.Item
            label="Старая цена"
            name="oldPrice"
            rules={[{ required: false }]}
          >
            <Input type="number" min={0} />
          </Form.Item>
          <Form.Item label="Метрики" name="policyIds" rules={[{ required: true, message: " " }]}>
            <Select mode="multiple" options={store.lookups?.policies.map((e) => ({ value: e.id, label: e.name }))} />
          </Form.Item>
          <Form.List name="limits">
            {(fields, { add, remove }) => {
              const selectedKeys =
                form
                  .getFieldValue("limits")
                  ?.map((l: any) => l?.key)
                  .filter(Boolean) || [];

              const availableLimitOptions =
                store.lookups?.limits
                  .filter((lim) => !selectedKeys.includes(lim.value))
                  .map((lim) => ({
                    label: lim.title,
                    value: lim.value,
                  })) || [];

              return (
                <>
                  <div style={{ marginBottom: 8, fontWeight: 500 }}>Ограничения</div>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                      <Form.Item
                        {...restField}
                        name={[name, "key"]}
                        rules={[{ required: true, message: " " }]}
                        style={{ width: "100%" }}
                      >
                        <Select
                          placeholder="Тип"
                          options={store.lookups?.limits.map((lim) => ({
                            label: lim.title,
                            value: lim.value,
                            disabled: selectedKeys.includes(lim.value) && form.getFieldValue(["limits", name, "key"]) !== lim.value,
                          }))}
                        />
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        name={[name, "value"]}
                        rules={[{ required: true, message: " " }]}
                        style={{ width: 120, minWidth: 120 }}
                      >
                        <Input type="number" placeholder="Значение" />
                      </Form.Item>

                      <Button onClick={() => remove(name)} danger type="text">
                        Удалить
                      </Button>
                    </div>
                  ))}
                  {availableLimitOptions.length > 0 && (
                    <Form.Item>
                      <Button type="dashed" onClick={() => add()} block>
                        Добавить ограничение
                      </Button>
                    </Form.Item>
                  )}
                </>
              );
            }}
          </Form.List>
          <Form.List name="tariffCountryPrices">
            {(fields, { add, remove }) => {
              const selectedCountries =
                form
                  .getFieldValue("tariffCountryPrices")
                  ?.map((c: any) => c?.countryId)
                  .filter(Boolean) || [];
              
              const availableCountries =
                store.lookups?.countries
                  ?.filter((c) => !selectedCountries.includes(c.id))
                  .map((c) => ({
                    label: c.name,
                    value: c.id,
                  })) || [];
                
              return (
                <>
                  <div style={{ marginBottom: 8, fontWeight: 500 }}>
                    Специальные цены по странам
                  </div>
              
                  {fields.map(({ key, name, ...restField }) => (
                    <div
                      key={key}
                      style={{ display: "flex", gap: 8, marginBottom: 8 }}
                    >
                      <Form.Item
                        {...restField}
                        name={[name, "countryId"]}
                        rules={[{ required: true, message: " " }]}
                        style={{ width: "50%" }}
                      >
                        <Select
                          placeholder="Страна"
                          options={store.lookups?.countries.map((c) => ({
                            label: c.name,
                            value: c.id,
                            disabled: selectedCountries.includes(c.id) && form.getFieldValue(["tariffCountryPrices", name, "countryId"]) !== c.id,
                          }))}
                        />
                      </Form.Item>
                        
                      <Form.Item
                        {...restField}
                        name={[name, "oldPrice"]}
                        rules={[{ required: false }]}
                        style={{ width: 120 }}
                      >
                        <Input type="number" placeholder="Старая" />
                      </Form.Item>
                        
                      <Form.Item
                        {...restField}
                        name={[name, "price"]}
                        rules={[{ required: true, message: " " }]}
                        style={{ width: 120 }}
                      >
                        <Input type="number" placeholder="Цена" />
                      </Form.Item>
                        
                      <Button
                        onClick={() => remove(name)}
                        danger
                        type="text"
                      >
                        Удалить
                      </Button>
                    </div>
                  ))}

                  {availableCountries.length > 0 && (
                    <Form.Item>
                      <Button type="dashed" onClick={() => add()} block>
                        Добавить страну
                      </Button>
                    </Form.Item>
                  )}
                </>
              );
            }}
          </Form.List>
        </Form>
      </Modal>
    </Scaffold>
  );
});

export default AdminTariffsView;